if (!customElements.get("m-product-inventory")) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        inventoryStatus: ".m-product-inventory__status",
        inventoryMessage: ".m-product-inventory__message",
        indicatorBar: ".m-product-inventory__indicator-bar",
      };

      this.domNodes = {};
      this.variantSubscriptionCreated = false;
    }

    connectedCallback() {
      Object.entries(this.selectors).forEach(([key, selector]) => {
        this.domNodes[key] = this.querySelector(selector);
      });

      this.productId = this.dataset.productId;
      this.inventoryVisibility = this.dataset.inventoryVisibility;
      this.hideStockBackordered =
        this.dataset.hideStockBackordered === "true";

      this.handleVariantChange();

      this.updateInventoryStatus(
        Number.parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === "true",
        this.dataset.inventoryPolicy
      );
    }

    handleVariantChange() {
      if (
        this.variantSubscriptionCreated ||
        !window.MinimogEvents ||
        typeof window.MinimogEvents.subscribe !== "function"
      ) {
        return;
      }

      window.MinimogEvents.subscribe(
        `${this.productId}__VARIANT_CHANGE`,
        (variant) => {
          if (!this.isConnected) return;

          if (!variant) {
            this.updateInventoryStatus(0, false, "deny");
            return;
          }

          const jsonElement = this.querySelector(
            'script[type="application/json"]'
          );

          if (!jsonElement) return;

          let inventoryData;

          try {
            inventoryData = JSON.parse(jsonElement.textContent);
          } catch (error) {
            console.warn("No se pudieron leer los datos de inventario.", error);
            return;
          }

          if (!Array.isArray(inventoryData)) return;

          const variantInventory = inventoryData.find(
            (item) => String(item.id) === String(variant.id)
          );

          if (!variantInventory) {
            this.classList.add("m:hidden");
            return;
          }

          this.updateInventoryStatus(
            Number.parseInt(variantInventory.inventory_quantity, 10),
            variantInventory.available,
            variantInventory.inventory_policy
          );
        }
      );

      this.variantSubscriptionCreated = true;
    }

    updateInventoryStatus(count, available, inventoryPolicy) {
      if (!Number.isFinite(count)) {
        this.classList.add("m:hidden");
        return;
      }

      const configuredThreshold = Number(this.dataset.inventoryThreshold);
      const threshold = Number.isFinite(configuredThreshold)
        ? Math.max(0, configuredThreshold)
        : 10;
      let status;

      if (!available) {
        status = "outofstock";
      } else if (count <= 0) {
        status = inventoryPolicy === "continue"
          ? "backordered"
          : "instock";
      } else if (count <= threshold) {
        status = "low";
      } else {
        status = "normal";
      }

      this.setAttribute("data-status", status);
      this.dataset.inventoryQuantity = String(count);
      this.dataset.variantAvailable = String(Boolean(available));

      if (inventoryPolicy) {
        this.dataset.inventoryPolicy = inventoryPolicy;
      }

      let show;

      if (this.inventoryVisibility === "always") {
        show = !(
          status === "backordered" &&
          this.hideStockBackordered
        );
      } else {
        show = status === "low";
      }

      this.classList.toggle("m:hidden", !show);

      if (!show) return;

      const message = this.domNodes.inventoryMessage;
      const inventoryStatus = this.domNodes.inventoryStatus;

      if (!message) return;

      const showUrgency =
        status === "low" &&
        available &&
        inventoryPolicy === "deny";

      if (showUrgency) {
        this.renderUrgency(message, count);

        if (inventoryStatus) {
          inventoryStatus.textContent = "";
          inventoryStatus.classList.add("m:hidden");
        }
      } else {
        const content = this.getInventoryText()[status];

        message.innerHTML = content.message || "";

        if (inventoryStatus) {
          let text = "";

          if (content.show_count && content.text_html) {
            text = content.text_html
              .split("{{ quantity }}")
              .join(String(count));
          } else if (!content.show_count && content.text) {
            text = content.text;
          }

          inventoryStatus.innerHTML = text;
          inventoryStatus.classList.toggle("m:hidden", !text);
        }
      }

      this.handleIndicatorBar(status, count);
    }

    renderUrgency(message, count) {
      message.innerHTML = "";

      const paragraph = document.createElement("p");

      paragraph.style.margin = "0";
      paragraph.style.color = "#b45309";
      paragraph.style.fontWeight = "600";

      const icon = document.createElement("span");
      icon.setAttribute("aria-hidden", "true");
      icon.textContent = "⚠ ";

      const units = count === 1 ? "unidad" : "unidades";

      paragraph.appendChild(icon);
      paragraph.appendChild(
        document.createTextNode(
          `¡Quedan solo ${count} ${units}!`
        )
      );

      message.appendChild(paragraph);
    }

    handleIndicatorBar(status, count) {
      const indicatorBar = this.domNodes.indicatorBar;

      if (!indicatorBar) return;

      const bar = indicatorBar.querySelector("span");

      if (!bar) return;

      const total = Number(this.dataset.total);
      let width;

      if (status === "backordered" || status === "outofstock") {
        width = 0;
      } else if (
        status === "instock" ||
        !Number.isFinite(total) ||
        total <= 0 ||
        count >= total
      ) {
        width = 100;
      } else {
        width = Math.min(
          100,
          Math.max(5, (count / total) * 100)
        );
      }

      bar.style.width = `${width.toFixed(1)}%`;
    }

    getInventoryText() {
      const strings = window.MinimogStrings || {};
      const showCount = this.dataset.showInventoryCount;

      return {
        normal: {
          text: strings.inStock || "En stock",
          text_html: strings.inventoryQuantityHtml || "",
          message: this.dataset.normalText || "<p>En stock</p>",
          show_count: showCount === "always",
        },

        instock: {
          text: strings.inStock || "En stock",
          message: this.dataset.normalText || "<p>En stock</p>",
          show_count: false,
        },

        low: {
          text: strings.lowStock || "Pocas unidades",
          text_html: strings.inventoryLowQuantityHtml || "",
          message: this.dataset.lowText || "<p>Pocas unidades</p>",
          show_count: showCount !== "never",
        },

        outofstock: {
          text: strings.outOfStock || "Sin stock",
          message: this.dataset.outofstockText || "<p>Sin stock</p>",
          show_count: false,
        },

        backordered: {
          text: "",
          message:
            this.dataset.backorderedText ||
            "<p>Disponible bajo pedido</p>",
          show_count: false,
        },
      };
    }
  }

  customElements.define("m-product-inventory", ProductInventory);
}
